import { create } from 'zustand';
import { dashboardAPI, isOnline } from '../services/api';

/**
 * Dashboard Store
 * مخزن Zustand لإدارة حالة الصفحة الرئيسية والإحصائيات
 *
 * Protocols Applied:
 * - P-STATE-001: Zustand State Management
 * - P-DATA-001: Clear Data Structure
 * - P-UX-001: User Experience (loading states)
 * - P010: Error Handling
 * - P-API-001: RESTful API Integration
 * - P-OFFLINE-001: Offline-First Strategy
 */

const useDashboardStore = create((set, get) => ({
  // ==================== STATE ====================

  statistics: {
    todaySales: { count: 0, total: 0, itemsCount: 0, loading: false },
    todayCustomers: { count: 0, loading: false },
    inventory: { totalProducts: 0, lowStockCount: 0, loading: false },
    collections: { count: 0, total: 0, loading: false },
    returns: { count: 0, total: 0, loading: false }
  },

  recentActivity: [],
  recentActivityLoading: false,

  salesChartData: [],
  salesChartLoading: false,

  error: null,

  // ==================== ACTIONS ====================

  loadStatistics: async () => {
    try {
      set({ error: null });

      // Try API first if online
      if (isOnline()) {
        try {
          const response = await dashboardAPI.getStats();
          const stats = response.data.data || response.data;

          // Map API response to store format
          set({
            statistics: {
              todaySales: {
                count: stats.today?.invoices_count || 0,
                total: stats.today?.invoices_total || 0,
                itemsCount: stats.today?.items_sold || 0,
                loading: false
              },
              todayCustomers: {
                count: stats.today?.customers_visited_count || 0,
                loading: false
              },
              inventory: {
                totalProducts: stats.overall?.total_products || 0,
                lowStockCount: stats.overall?.low_stock_products || 0,
                loading: false
              },
              collections: {
                count: stats.today?.receipts_count || 0,
                total: stats.today?.receipts_total || 0,
                loading: false
              },
              returns: {
                count: stats.overall?.returns_count || 0,
                total: stats.overall?.returns_total || 0,
                loading: false
              }
            }
          });
          return;
        } catch (apiError) {
          console.error('API stats failed, falling back to local calculation:', apiError);
          set({ error: apiError.message });
        }
      }

      // Fallback: Calculate from local stores
      const { default: useInvoiceStore } = await import('../stores/useInvoiceStore');
      const { default: useProductStore } = await import('../stores/useProductStore');

      const invoiceStore = useInvoiceStore.getState();
      const productStore = useProductStore.getState();

      // Today's Sales
      const today = new Date().toISOString().split('T')[0];
      const todayInvoices = invoiceStore.invoices.filter(inv => inv.date?.startsWith(today));

      const todaySales = {
        count: todayInvoices.length,
        total: todayInvoices.reduce((sum, inv) => sum + (inv.total || 0), 0),
        itemsCount: todayInvoices.reduce((sum, inv) => sum + (inv.items?.length || 0), 0),
        loading: false
      };

      // Today's Customers (unique customers from today's invoices)
      const todayCustomers = {
        count: new Set(todayInvoices.map(inv => inv.customerId)).size,
        loading: false
      };

      // REAL Inventory (من useProductStore!)
      const inventory = {
        totalProducts: productStore.getTotalProducts(),
        lowStockCount: productStore.getLowStockProducts().length,
        loading: false
      };

      // Collections
      const todayPayments = todayInvoices.flatMap(inv =>
        invoiceStore.payments.filter(p => p.invoiceId === inv.id)
      );

      const collections = {
        count: todayPayments.length,
        total: todayPayments.reduce((sum, p) => sum + (p.amount || 0), 0),
        loading: false
      };

      // Returns (مؤقتاً 0)
      const returns = { count: 0, total: 0, loading: false };

      set({
        statistics: { todaySales, todayCustomers, inventory, collections, returns }
      });

    } catch (error) {
      console.error('[Dashboard] Error loading statistics:', error);
      set({ error: error.message });
    }
  },

  getRecentActivity: async (limit = 10) => {
    try {
      set({ recentActivityLoading: true, error: null });

      // Try API first if online
      if (isOnline()) {
        try {
          const response = await dashboardAPI.getRecentActivity(limit);
          const activities = response.data.data || response.data;
          set({ recentActivity: activities, recentActivityLoading: false });
          return;
        } catch (apiError) {
          console.error('API recent activity failed, falling back to local:', apiError);
        }
      }

      // Fallback: Use local data
      const { default: useInvoiceStore } = await import('../stores/useInvoiceStore');
      const invoiceStore = useInvoiceStore.getState();

      const recentInvoices = [...invoiceStore.invoices]
        .sort((a, b) => new Date(b.createdAt || b.date) - new Date(a.createdAt || a.date))
        .slice(0, limit)
        .map(inv => ({
          id: inv.id,
          type: 'invoice',
          title: `فاتورة ${inv.invoiceNumber}`,
          customer: inv.customer?.name || 'عميل غير محدد',
          amount: inv.total,
          date: inv.date,
          createdAt: inv.createdAt,
          icon: 'FileText',
          color: 'blue'
        }));

      const recentPayments = [...invoiceStore.payments]
        .sort((a, b) => new Date(b.paymentDate) - new Date(a.paymentDate))
        .slice(0, limit)
        .map(payment => {
          const invoice = invoiceStore.invoices.find(inv => inv.id === payment.invoiceId);
          return {
            id: payment.id,
            type: 'payment',
            title: `دفعة ${payment.paymentMethod}`,
            customer: invoice?.customer?.name || 'عميل غير محدد',
            amount: payment.amount,
            date: payment.paymentDate,
            createdAt: payment.createdAt,
            icon: 'DollarSign',
            color: 'green'
          };
        });

      const allActivity = [...recentInvoices, ...recentPayments]
        .sort((a, b) => new Date(b.createdAt || b.date) - new Date(a.createdAt || a.date))
        .slice(0, limit);

      set({ recentActivity: allActivity, recentActivityLoading: false });

    } catch (error) {
      console.error('[Dashboard] Error loading recent activity:', error);
      set({ error: error.message, recentActivityLoading: false });
    }
  },

  loadSalesChartData: async (days = 7) => {
    try {
      set({ salesChartLoading: true, error: null });

      // Try API first if online
      if (isOnline()) {
        try {
          const response = await dashboardAPI.getSalesChart(days);
          const chartData = response.data.data || response.data;
          set({ salesChartData: chartData, salesChartLoading: false });
          return;
        } catch (apiError) {
          console.error('API sales chart failed, falling back to local:', apiError);
        }
      }

      // Fallback: Use local data
      const { default: useInvoiceStore } = await import('../stores/useInvoiceStore');
      const invoiceStore = useInvoiceStore.getState();

      const chartData = [];
      for (let i = days - 1; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];

        const dayInvoices = invoiceStore.invoices.filter(inv => inv.date?.startsWith(dateStr));
        const dayTotal = dayInvoices.reduce((sum, inv) => sum + (inv.total || 0), 0);

        chartData.push({
          date: dateStr,
          label: date.toLocaleDateString('ar-EG', { weekday: 'short', day: 'numeric', month: 'short' }),
          sales: dayTotal,
          count: dayInvoices.length
        });
      }

      set({ salesChartData: chartData, salesChartLoading: false });

    } catch (error) {
      console.error('[Dashboard] Error loading sales chart:', error);
      set({ error: error.message, salesChartLoading: false });
    }
  },

  clearError: () => set({ error: null }),

  refreshDashboard: async () => {
    await Promise.all([
      get().loadStatistics(),
      get().getRecentActivity(),
      get().loadSalesChartData()
    ]);
  }
}));

export default useDashboardStore;
